import {
  SOLD_LISTING_TYPE,
  type SqmCsvParseResult,
  type ValuationDataConnectionCheck,
  type ValuationDataSourceRecord,
  type ValuationIngressRun,
  type ValuationSourceCoverage,
} from "@repo/types/valuation";
import { supabase } from "@/integrations/supabase/client";

export interface SqmImportPreview {
  rows: SqmCsvParseResult["rows"];
  totalRowsRead: number;
  rejectedRows: number;
  warningCount: number;
  warnings: string[];
  headers: string[];
}

async function exactCount(
  query: PromiseLike<{ count: number | null; error: Error | null }>,
): Promise<{
  count: number;
  errorMessage: string | null;
}> {
  const { count, error } = await query;
  return { count: count ?? 0, errorMessage: error?.message ?? null };
}

async function invokeValuationDataSources<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("valuation-data-sources", { body });
  if (error) {
    throw error;
  }
  if (data?.success === false) {
    throw new Error(data.error ?? "Valuation data source request failed");
  }
  return data as T;
}

export async function fetchValuationDataSources(): Promise<ValuationDataSourceRecord[]> {
  const { data, error } = await (supabase as any)
    .from("valuation_data_sources")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return data ?? [];
}

export async function checkValuationDataSource(
  sourceSlug: string,
): Promise<ValuationDataConnectionCheck> {
  const data = await invokeValuationDataSources<{
    success: boolean;
    check: ValuationDataConnectionCheck;
  }>({ action: "check-source", sourceSlug });
  return data.check;
}

export async function fetchValuationIngressRuns(limit = 50): Promise<ValuationIngressRun[]> {
  const { data, error } = await (supabase as any)
    .from("valuation_ingress_runs")
    .select("*, valuation_data_sources(slug,name)")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return data ?? [];
}

/**
 * Resume-aware lookup: pulls the set of chunk_index values already
 * `status='completed'` for a given (source_file_name, total_chunks).
 * Bypasses the 50-row display cap of fetchValuationIngressRuns so the
 * resume calculation stays correct across long upload sessions.
 *
 * Requires `imported_count > 0` in addition to status='completed'. The
 * edge function marks a chunk "completed" whenever it ran without
 * throwing — including a chunk where every row failed parseSqmCsv's
 * address/suburb/postcode validation and imported_count landed at 0 (see
 * packages/types/src/valuation.ts parseSqmCsv). Without this filter, a
 * chunk that zero-imported for any transient reason (bad file selected,
 * a mid-read truncation, a since-fixed bug) is marked "safe to skip"
 * forever: every future retry of the same file skips it again, so the
 * import can get permanently stuck reporting "completed" with 0 rows
 * staged with no way to recover short of renaming the file. Treating a
 * zero-imported chunk as NOT completed makes retries self-healing —
 * this is suspected to be exactly how SA.csv/TAS.csv's "completed with
 * 0 rows staged" ingress runs (see .cursor/plans/
 * handover-independence-triage-2026-09-25.md item 13) got stuck; the
 * mapping itself parses both files with 0% rejection when re-tested
 * directly (see apps/web/src/lib/valuations/valuation.test.ts).
 */
export async function fetchCompletedChunksForFile(
  sourceFileName: string,
  totalChunks: number,
): Promise<Set<number>> {
  const PAGE = 1000;
  const completed = new Set<number>();
  let from = 0;
  // We use the supabase client directly because PostgREST handles the
  // filter pushdown server-side — `chunk_index` is indexed via the
  // existing source_id index and (status, source_file_name) is selective
  // enough on real data that the scan is fast.
  while (true) {
    const to = from + PAGE - 1;
    const { data, error } = await (supabase as any)
      .from("valuation_ingress_runs")
      .select("chunk_index")
      .eq("status", "completed")
      .eq("source_file_name", sourceFileName)
      .eq("total_chunks", totalChunks)
      .gt("imported_count", 0)
      .not("chunk_index", "is", null)
      .range(from, to);
    if (error) throw error;
    const rows = (data ?? []) as Array<{ chunk_index: number | null }>;
    for (const row of rows) {
      if (typeof row.chunk_index === "number") completed.add(row.chunk_index);
    }
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return completed;
}

export async function previewSqmImport(input: {
  csvText: string;
  sourceFileName: string;
  sourceState?: string | null;
  maxRows?: number;
}): Promise<SqmImportPreview> {
  const data = await invokeValuationDataSources<{
    success: boolean;
    preview: SqmImportPreview;
  }>({
    action: "preview-sqm-import",
    csvText: input.csvText,
    sourceFileName: input.sourceFileName,
    sourceState: input.sourceState ?? null,
    maxRows: input.maxRows ?? 25,
  });
  return data.preview;
}

export async function runSqmImport(input: {
  csvText: string;
  sourceFileName: string;
  sourceState?: string | null;
  chunkIndex?: number;
  totalChunks?: number;
  // Offset added to each parsed row's within-chunk index so
  // source_row_number is unique across chunks of the same file.
  baseRowNumber?: number;
}): Promise<ValuationIngressRun> {
  const data = await invokeValuationDataSources<{
    success: boolean;
    run: ValuationIngressRun;
  }>({
    action: "run-sqm-import",
    csvText: input.csvText,
    sourceFileName: input.sourceFileName,
    sourceState: input.sourceState ?? null,
    chunkIndex: input.chunkIndex ?? null,
    totalChunks: input.totalChunks ?? null,
    baseRowNumber: input.baseRowNumber ?? null,
  });
  return data.run;
}

export interface SqmBackfillResult {
  scopedCount: number;
  consideredCount: number;
  skippedInvalidAddress: number;
  skippedAlreadySnapshotted: number;
  propertiesUpserted: number;
  snapshotsInserted: number;
  dryRun: boolean;
  state: string | null;
  sourceFileName: string | null;
}

export interface SqmStagingFileSummary {
  source_file_name: string;
  source_state: string | null;
  total_rows: number;
  unique_sale_events: number;
  duplicate_sale_event_groups: number;
  duplicate_extra_rows: number;
  missing_sold_price: number;
  missing_sold_date: number;
  missing_beds: number;
  missing_baths: number;
  missing_cars: number;
  missing_area_sqm: number;
  backfilled_count: number;
  latest_ingress_at: string | null;
}

export async function fetchSqmStagingAnalytics(): Promise<{ files: SqmStagingFileSummary[] }> {
  return invokeValuationDataSources<{
    success: boolean;
    analytics: { files: SqmStagingFileSummary[] };
  }>({ action: "sqm-staging-analytics" }).then((data) => data.analytics);
}

export async function backfillSqmToProperties(input: {
  state?: string | null;
  sourceFileName?: string | null;
  limit?: number;
  dryRun?: boolean;
}): Promise<SqmBackfillResult> {
  const data = await invokeValuationDataSources<{
    success: boolean;
    backfill: SqmBackfillResult;
  }>({
    action: "backfill-sqm-to-properties",
    state: input.state ?? null,
    sourceFileName: input.sourceFileName ?? null,
    limit: input.limit ?? 10000,
    dryRun: input.dryRun === true,
  });
  return data.backfill;
}

export async function fetchValuationSourceCoverage(input: {
  suburbId?: string | null;
  suburbName?: string | null;
  postcode?: string | null;
}): Promise<ValuationSourceCoverage[]> {
  const sources = await fetchValuationDataSources();
  const sourceBySlug = new Map(sources.map((source) => [source.slug, source]));
  const coverage: ValuationSourceCoverage[] = [];
  const postcode = input.postcode?.trim();
  const suburbName = input.suburbName?.trim();

  // Both SQM and internal coverage now count property_snapshots rows
  // (SQM-backfilled rows are tagged adapter_slug='sqm-back-history').
  // The reciprocal filter on internal_property_snapshots prevents double
  // counting the same row across both cards. Both count sold listings only,
  // matching what run-valuation-algorithm reads.
  const sqmSource = sourceBySlug.get("sqm_back_history");
  if (sqmSource && postcode) {
    let sqmQuery = (supabase as any)
      .from("property_snapshots")
      .select("id,properties!inner(id)", { count: "exact", head: true })
      .not("sale_price", "is", null)
      .ilike("listing_type", SOLD_LISTING_TYPE)
      .eq("adapter_slug", "sqm-back-history")
      .eq("properties.postcode", postcode);
    if (suburbName) {
      sqmQuery = sqmQuery.ilike("properties.suburb", suburbName);
    }
    const rows = await exactCount(sqmQuery);
    coverage.push({
      sourceSlug: sqmSource.slug,
      sourceName: sqmSource.name,
      status: sqmSource.status,
      recordCount: rows.count,
      pricedRecordCount: rows.count,
      latestPeriod: null,
      coverageLabel: "SQM-backfilled snapshots",
      notes: rows.errorMessage,
    });
  }

  const propertySource = sourceBySlug.get("internal_property_snapshots");
  if (propertySource && postcode) {
    let propertyQuery = (supabase as any)
      .from("property_snapshots")
      .select("id,properties!inner(id)", { count: "exact", head: true })
      .not("sale_price", "is", null)
      .ilike("listing_type", SOLD_LISTING_TYPE)
      .neq("adapter_slug", "sqm-back-history")
      .eq("properties.postcode", postcode);
    if (suburbName) {
      propertyQuery = propertyQuery.ilike("properties.suburb", suburbName);
    }
    const rows = await exactCount(propertyQuery);
    coverage.push({
      sourceSlug: propertySource.slug,
      sourceName: propertySource.name,
      status: propertySource.status,
      recordCount: rows.count,
      pricedRecordCount: rows.count,
      latestPeriod: null,
      coverageLabel: "scraped sale snapshots",
      notes: rows.errorMessage,
    });
  }

  const profileSource = sourceBySlug.get("internal_suburb_profile_snapshots");
  if (profileSource && input.suburbId) {
    const rows = await exactCount(
      (supabase as any)
        .from("suburb_profile_snapshots")
        .select("id", { count: "exact", head: true })
        .eq("suburb_id", input.suburbId),
    );
    coverage.push({
      sourceSlug: profileSource.slug,
      sourceName: profileSource.name,
      status: profileSource.status,
      recordCount: rows.count,
      latestPeriod: null,
      coverageLabel: "suburb profile months",
      notes: rows.errorMessage,
    });
  }

  for (const source of sources.filter((item) => item.category === "official_api")) {
    const rows = await exactCount(
      (supabase as any)
        .from("valuation_source_observations")
        .select("id", { count: "exact", head: true })
        .eq("source_id", source.id),
    );
    coverage.push({
      sourceSlug: source.slug,
      sourceName: source.name,
      status: source.status,
      recordCount: rows.count,
      latestPeriod: null,
      coverageLabel: "macro observations",
      notes: rows.errorMessage,
    });
  }

  return coverage;
}
